import { redirect } from '@tanstack/react-router'
import { queryClient } from 'app/providers/queryClient'
import { placesQueryOptions } from 'entities/office-space/model/usePlacesQuery'
import { bootstrapWebviewAuth } from 'pages/webview/model/useWebviewAuthBootstrap'
import { getMpulseToken, hasAnyAuthToken, hasMpulseToken } from 'shared/lib/auth'
import {
  hasWebviewTokenJsChannel,
  requestWebviewTokenViaJsChannel,
  waitForWebviewTokenJsChannel,
} from 'shared/lib/webviewAuthTokenChannel'

function isUnauthorizedError(error: unknown) {
  if (!error || typeof error !== 'object') return false
  if (!('status' in error)) return false
  return (error as { status?: unknown }).status === 401
}

function isRedirectToAuthResult(
  result: Awaited<ReturnType<typeof bootstrapWebviewAuth>>
): result is { ok: false; redirectToAuth: true } {
  return !result.ok && 'redirectToAuth' in result && result.redirectToAuth
}

function isErrorResult(
  result: Awaited<ReturnType<typeof bootstrapWebviewAuth>>
): result is { ok: false; error: string } {
  return !result.ok && 'error' in result
}

async function finalizeWebviewSession(jsChannelToken: string | null, preloadPlaces: boolean) {
  const bootstrapResult = await bootstrapWebviewAuth(jsChannelToken)

  if (isRedirectToAuthResult(bootstrapResult)) {
    throw redirect({ to: '/auth' })
  }

  if (isErrorResult(bootstrapResult)) {
    throw new Error(bootstrapResult.error)
  }

  if (preloadPlaces) {
    // A desks request can fail with the normalized API error object. Let the
    // page-level query render its established retry/maintenance state instead
    // of allowing that object to escape the route loader and crash MatchInner.
    await queryClient.prefetchQuery(placesQueryOptions())
  }
}

export async function beforeLoadProtectedWebviewSession() {
  if (hasAnyAuthToken() || hasMpulseToken()) {
    return
  }

  const canRequestTokenViaJsChannel =
    hasWebviewTokenJsChannel() || (await waitForWebviewTokenJsChannel())

  if (canRequestTokenViaJsChannel) {
    return
  }

  throw redirect({ to: '/auth' })
}

export async function loadProtectedWebviewSession({
  preloadPlaces = false,
}: {
  preloadPlaces?: boolean
} = {}) {
  let initialJsChannelToken: string | null = null
  let retryJsChannelToken: string | null = null

  if (hasWebviewTokenJsChannel()) {
    initialJsChannelToken = await requestWebviewTokenViaJsChannel()
  }

  try {
    await finalizeWebviewSession(initialJsChannelToken, preloadPlaces)
    return
  } catch (error) {
    if (!hasWebviewTokenJsChannel()) {
      throw error
    }

    if (!isUnauthorizedError(error) && !(error instanceof Error)) {
      throw error
    }

    retryJsChannelToken = await requestWebviewTokenViaJsChannel()
    if (!retryJsChannelToken) {
      throw error
    }
  }

  await finalizeWebviewSession(retryJsChannelToken, preloadPlaces)
}

export async function retryWebviewAuthOrCloseApp() {
  if (hasAnyAuthToken() || getMpulseToken()?.trim()) {
    window.location.reload()
    return
  }

  window.CLOSE_WEBVIEW?.postMessage('close')

  if (!window.CLOSE_WEBVIEW) {
    if (window.history.length > 1) {
      window.history.back()
      return
    }

    window.close()
  }
}
